"use client"

import { type Recipe, effectiveWay } from "@/lib/cover-press/engine"

import { PressCanvas } from "./press-canvas"

/** Placeholder playlist copy so the surfaces read as shipped, not as swatches. */
const SAMPLE = {
  title: "Long Sunday Drift",
  curator: "Willa Hartigan",
  meta: "48 tracks · 6 contributors",
}

/** Secondary pulls wait a beat so the hero stays responsive while dragging. */
const SETTLE_MS = 350

interface InSituProps {
  recipe: Recipe
}

/**
 * The same pull across every surface it ships to: playlist card, CD label,
 * poster and card back. Each is the current recipe painted at that surface's
 * aspect, framed the way the surface frames it. The ink and paper colours are
 * the recipe's own (they are the artwork, not chrome).
 */
export function InSitu({ recipe }: InSituProps) {
  const way = effectiveWay(recipe)
  const ink = way.inks[0][0]
  const paper = way.paper

  return (
    <div className="flex flex-wrap items-start gap-7">
      <Surface label="Playlist card">
        <div className="bg-primary ring-secondary w-[360px] max-w-full overflow-hidden rounded-lg shadow-xs ring-1">
          <PressCanvas
            recipe={{ ...recipe, format: "card" }}
            width={600}
            height={280}
            delayMs={SETTLE_MS}
            className="block w-full"
            aria-hidden
          />
          <div className="h-1" style={{ background: ink }} />
          <div className="relative px-5 py-4">
            <div
              className="ring-bg-primary absolute -top-6 right-4 flex size-11 items-center justify-center rounded-full ring-[3px]"
              style={{ background: ink }}
              aria-hidden
            >
              <span
                className="ml-0.5 size-0 border-y-[7px] border-l-[11px] border-y-transparent"
                style={{ borderLeftColor: paper }}
              />
            </div>
            <div className="text-primary font-display text-xl leading-tight font-semibold">
              {SAMPLE.title}
            </div>
            <div className="text-secondary mt-1 text-sm font-semibold">
              by {SAMPLE.curator}
            </div>
            <div className="text-tertiary mt-2 text-sm">{SAMPLE.meta}</div>
          </div>
        </div>
      </Surface>

      <Surface label="CD label">
        <div className="relative size-52">
          <PressCanvas
            recipe={{ ...recipe, format: "cd" }}
            width={380}
            height={380}
            delayMs={SETTLE_MS}
            className="block size-52 rounded-full"
            aria-hidden
          />
          <div className="bg-primary ring-primary absolute top-1/2 left-1/2 size-[72px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-inset" />
          <div className="bg-secondary ring-primary absolute top-1/2 left-1/2 size-[26px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-inset" />
        </div>
      </Surface>

      <Surface label="Poster">
        <div className="relative w-[200px] shadow-md">
          <PressCanvas
            recipe={{ ...recipe, format: "poster" }}
            width={300}
            height={450}
            delayMs={SETTLE_MS}
            className="block h-[300px] w-[200px]"
            aria-hidden
          />
          <div
            className="absolute inset-x-0 bottom-0 px-3.5 pt-2.5 pb-3"
            style={{ background: ink, color: paper }}
          >
            <div className="font-display text-lg leading-tight font-semibold">
              {SAMPLE.title}
            </div>
            <div className="mt-1 text-[9px] font-semibold tracking-[0.14em] uppercase opacity-85">
              {SAMPLE.curator} · 48 tracks
            </div>
          </div>
        </div>
      </Surface>

      <Surface label="Card back">
        <div className="bg-primary w-[152px] rounded-xl p-2 shadow-xs">
          <PressCanvas
            recipe={{ ...recipe, format: "back" }}
            width={260}
            height={368}
            delayMs={SETTLE_MS}
            className="block h-[187px] w-[132px] rounded border-[1.5px]"
            style={{ borderColor: ink }}
            aria-hidden
          />
        </div>
      </Surface>
    </div>
  )
}

function Surface({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <figure className="m-0 flex flex-col gap-2">
      {children}
      <figcaption className="text-quaternary text-xs font-medium tracking-wide uppercase">
        {label}
      </figcaption>
    </figure>
  )
}
